/**
 * useSwimlaneData Hook
 *
 * Manages data loading for a single TreeMap swimlane.
 * Each swimlane independently fetches its children using the context children API.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { Sphere } from './types';
import type { ContextLevel, ChildrenResult, SphereNode } from 'types/context';

export interface UseSwimlaneDataParams {
  parentSphereId: string;
  levelConfig: ContextLevel;
  apiClient: ApiClient | null;
  enabled?: boolean;  // Allow disabling (e.g., when swimlane is collapsed)
  defaultPageSize?: number;
  /** Context system name for the children endpoint */
  contextSystemName?: string;
  /** Root sphere ID for the context execution */
  rootSphereId?: string;
}

export interface UseSwimlaneDataReturn {
  spheres: Sphere[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  nextToken?: string;
  totalCount: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Convert a SphereNode to a TreeMap Sphere
 */
function sphereNodeToSphere(node: SphereNode, parentId: string): Sphere {
  const perspectives: Record<string, string[]> = {};
  if (node.assignedPerspectives) {
    for (const ap of node.assignedPerspectives) {
      const systemName = ap.perspectiveSystemName;
      if (systemName && ap.assignedSpheres) {
        const assigneeIds = ap.assignedSpheres.map(a => a.id).filter(Boolean);
        if (assigneeIds.length > 0) {
          perspectives[systemName] = assigneeIds;
        }
      }
    }
  }

  return {
    id: node.id,
    schema_type: node.schemaType || '',
    properties: node.properties as Record<string, any>,
    parent_id: parentId,
    organization_id: '',
    label: node.label,
    glyph: node.glyph,
    glyphColor: node.glyphColor,
    perspectives: Object.keys(perspectives).length > 0 ? perspectives : undefined,
    ...(node.status ? { statusPoint: node.status } : {}),
    ...(node.flow ? { flowData: node.flow } : {}),
  };
}

/**
 * Hook for loading data for a single swimlane using context children API
 */
export function useSwimlaneData({
  parentSphereId,
  levelConfig,
  apiClient,
  enabled = true,
  defaultPageSize = 5,
  contextSystemName,
  rootSphereId
}: UseSwimlaneDataParams): UseSwimlaneDataReturn {

  const [spheres, setSpheres] = useState<Sphere[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Track if initial load has happened
  const hasLoadedRef = useRef(false);
  const inflightRequestRef = useRef(false);

  /**
   * Load children for this swimlane using context children endpoint
   */
  const loadChildren = useCallback(async (token?: string, mode: 'append' | 'overwrite' = 'append') => {
    if (!apiClient || !enabled || inflightRequestRef.current) {
      return;
    }

    if (!contextSystemName || !rootSphereId) {
      return;
    }

    inflightRequestRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Build context children endpoint URL
      const queryParams = new URLSearchParams();
      queryParams.set('parentSphereId', parentSphereId);
      queryParams.set('level', String(levelConfig.order));
      queryParams.set('rootSphereId', rootSphereId);
      queryParams.set('limit', String(defaultPageSize));
      if (token) {
        queryParams.set('token', token);
      }

      const url = `/contexts/${encodeURIComponent(contextSystemName)}/execute/children?${queryParams.toString()}`;

      const response = await apiClient.get<ChildrenResult>(url);

      // Convert SphereNode children to Sphere format
      const newSpheres = response.children.map(child =>
        sphereNodeToSphere(child, parentSphereId)
      );

      const newToken = response.pagination?.nextToken;
      const count = response.pagination?.totalCount;

      // Update state
      setSpheres(prev => mode === 'overwrite' ? newSpheres : [...prev, ...newSpheres]);
      setNextToken(newToken);
      setHasMore(response.pagination?.hasMore ?? false);
      if (count === undefined) {
        setTotalCount(prev => mode === 'overwrite' ? newSpheres.length : prev + newSpheres.length);
      } else {
        setTotalCount(count);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to load children');
    } finally {
      setLoading(false);
      inflightRequestRef.current = false;
    }
  }, [apiClient, enabled, parentSphereId, levelConfig, defaultPageSize, contextSystemName, rootSphereId]);

  /**
   * Load initial data when swimlane mounts or becomes enabled
   */
  useEffect(() => {
    if (enabled && !hasLoadedRef.current && apiClient && contextSystemName && rootSphereId) {
      hasLoadedRef.current = true;
      loadChildren(undefined, 'overwrite');
    }
  }, [enabled, apiClient, loadChildren, parentSphereId, contextSystemName, rootSphereId]);

  /**
   * Load more children (pagination)
   */
  const loadMore = useCallback(async () => {
    if (nextToken && !loading) {
      await loadChildren(nextToken, 'append');
    }
  }, [nextToken, loading, loadChildren]);

  /**
   * Refresh data (reload from beginning)
   */
  const refresh = useCallback(async () => {
    hasLoadedRef.current = false;
    await loadChildren(undefined, 'overwrite');
    hasLoadedRef.current = true;
  }, [loadChildren]);

  return {
    spheres,
    loading,
    error,
    hasMore,
    nextToken,
    totalCount,
    loadMore,
    refresh
  };
}
